import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaCheck,
  FaCloud,
  FaExclamationTriangle,
  FaHistory,
  FaImages,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaShoppingCart,
  FaSlidersH,
  FaStore,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

import {
  apiApproveVendorMediaPurchase,
  apiGetVendorMediaAdminRows,
  apiGetVendorMediaAudit,
  apiGetVendorMediaPurchases,
  apiGetVendorMediaSettings,
  apiRejectVendorMediaPurchase,
  apiUpdateVendorMediaSettings,
} from "../api";
import socket from "../utilities/socket";
import "./VendorMediaAdmin.css";
import SettingHelp from "./Settings/settingsHelp";
import AppSelect from "../components/ui/AppSelect";

const fmtBytes = (bytes = 0) => {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(value / 1024).toFixed(1)} KB`;
};

const fmtMoney = (amount, currency = "UGX") =>
  `${currency === "UGX" ? "USh" : currency} ${Number(
    amount || 0
  ).toLocaleString()}`;

const fmtDateTime = (date) => {
  if (!date) return "Not recorded";
  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const humanize = (value = "") =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const statusTone = (status = "") => {
  if (status === "APPROVED") return "green";
  if (status === "PAYMENT_SUCCESSFUL") return "blue";
  if (status === "PAYMENT_FAILED" || status === "REJECTED") return "red";
  if (status === "PAYMENT_INIT_FAILED") return "gold";
  return "muted";
};

const VendorMediaAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rows, setRows] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);
  // The server default (env over shipped) each field quotes.
  const [settingsDefaults, setSettingsDefaults] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({
    includedImages: "",
    enforcementMode: "observe",
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [usageSearch, setUsageSearch] = useState("");
  const [purchaseStatus, setPurchaseStatus] = useState("all");
  const [purchaseDialog, setPurchaseDialog] = useState({
    show: false,
    mode: "approve",
    purchase: null,
    reason: "Payment not confirmed",
  });

  const load = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [usageRes, purchaseRes, auditRes, settingsRes] = await Promise.all([
        apiGetVendorMediaAdminRows(),
        apiGetVendorMediaPurchases(),
        apiGetVendorMediaAudit(),
        apiGetVendorMediaSettings(),
      ]);
      setRows(usageRes.data.data?.rows || []);
      setPurchases(purchaseRes.data.data?.purchases || []);
      setAudit(auditRes.data.data?.audit || []);
      const liveSettings = settingsRes.data.data?.settings || null;
      setSettings(liveSettings);
      setSettingsDefaults(settingsRes.data.data?.defaults || null);
      if (liveSettings) {
        setSettingsDraft({
          includedImages: String(liveSettings.includedImages),
          enforcementMode: liveSettings.enforcementMode || "observe",
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load media billing"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const join = () => socket.emit("joinSuperadminRoom");
    const refresh = () => load(true);

    join();
    socket.on("connect", join);
    socket.on("vendorMedia:updated", refresh);

    return () => {
      socket.off("connect", join);
      socket.off("vendorMedia:updated", refresh);
    };
  }, [load]);

  const saveSettings = async () => {
    const includedImages = Number(settingsDraft.includedImages);
    if (!Number.isFinite(includedImages) || includedImages < 0) {
      toast.error("Free image allowance must be a number greater than or equal to 0");
      return;
    }

    setSavingSettings(true);
    try {
      const response = await apiUpdateVendorMediaSettings({
        includedImages,
        enforcementMode: settingsDraft.enforcementMode,
      });
      setSettings(response.data.data?.settings || null);
      toast.success("Media billing settings saved");
      await load(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const openPurchaseDialog = (mode, purchase) => {
    setPurchaseDialog({
      show: true,
      mode,
      purchase,
      reason: "Payment not confirmed",
    });
  };

  const closePurchaseDialog = () => {
    if (busyId) return;
    setPurchaseDialog((current) => ({ ...current, show: false }));
  };

  const confirmPurchaseAction = async () => {
    const purchase = purchaseDialog.purchase;
    if (!purchase) return;

    if (
      purchaseDialog.mode === "reject" &&
      !purchaseDialog.reason.trim()
    ) {
      toast.error("Add a reason before rejecting this purchase");
      return;
    }

    setBusyId(purchase._id);
    try {
      if (purchaseDialog.mode === "approve") {
        await apiApproveVendorMediaPurchase(purchase._id);
        toast.success("Image pack approved");
      } else {
        await apiRejectVendorMediaPurchase(
          purchase._id,
          purchaseDialog.reason.trim()
        );
        toast.success("Image pack rejected");
      }
      setPurchaseDialog((current) => ({ ...current, show: false }));
      await load(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (purchaseDialog.mode === "approve"
            ? "Approval failed"
            : "Rejection failed")
      );
    } finally {
      setBusyId(null);
    }
  };

  const metrics = useMemo(() => {
    return {
      activeImages: rows.reduce(
        (total, row) => total + Number(row.activeImages || 0),
        0
      ),
      remainingImages: rows.reduce(
        (total, row) => total + Number(row.remainingImages || 0),
        0
      ),
      storageBytes: rows.reduce(
        (total, row) => total + Number(row.storageBytes || 0),
        0
      ),
      pendingApprovals: purchases.filter(
        (purchase) =>
          purchase.status !== "APPROVED" && purchase.status !== "REJECTED"
      ).length,
    };
  }, [purchases, rows]);

  const filteredRows = useMemo(() => {
    const query = usageSearch.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter(
      (row) =>
        row.vendorName?.toLowerCase().includes(query) ||
        row.vendorType?.toLowerCase().includes(query) ||
        String(row._id || "").toLowerCase().includes(query)
    );
  }, [rows, usageSearch]);

  const filteredPurchases = useMemo(
    () =>
      purchases.filter(
        (purchase) =>
          purchaseStatus === "all" || purchase.status === purchaseStatus
      ),
    [purchaseStatus, purchases]
  );

  const purchaseStatuses = useMemo(
    () => [...new Set(purchases.map((purchase) => purchase.status).filter(Boolean))],
    [purchases]
  );

  const settingsDirty =
    settings &&
    (String(settings.includedImages) !== settingsDraft.includedImages ||
      (settings.enforcementMode || "observe") !==
        settingsDraft.enforcementMode);

  if (loading) {
    return (
      <main className="media-billing-workspace">
        <div className="media-billing-loading" role="status">
          <Spinner animation="border" />
          <strong>Loading media billing</strong>
          <span>Collecting quotas, purchases, and audit activity.</span>
        </div>
      </main>
    );
  }

  return (
    <main className="media-billing-workspace">
      {refreshing && (
        <div className="media-billing-refreshing" role="status">
          <Spinner animation="border" size="sm" />
          <span>Syncing billing data</span>
        </div>
      )}

      <header className="media-billing-header">
        <div className="media-billing-header__title">
          <span className="media-billing-header__eyebrow">Wallet operations</span>
          <h1>
            <span aria-hidden="true">
              <FaImages />
            </span>
            Vendor Media Billing
          </h1>
          <p>Control image allowances, monitor storage, and review paid packs.</p>
        </div>
        <button
          type="button"
          className="media-button media-button--ghost"
          onClick={() => load(true)}
          disabled={refreshing}
        >
          <FaSyncAlt className={refreshing ? "is-spinning" : ""} />
          Refresh
        </button>
      </header>

      <section className="media-billing-metrics" aria-label="Media billing summary">
        <article className="media-billing-metric media-billing-metric--blue">
          <div>
            <span>Vendors tracked</span>
            <strong>{rows.length}</strong>
          </div>
          <span className="media-billing-metric__icon">
            <FaStore />
          </span>
        </article>
        <article className="media-billing-metric media-billing-metric--green">
          <div>
            <span>Active images</span>
            <strong>{metrics.activeImages.toLocaleString()}</strong>
          </div>
          <span className="media-billing-metric__icon">
            <FaImages />
          </span>
        </article>
        <article className="media-billing-metric media-billing-metric--gold">
          <div>
            <span>Capacity remaining</span>
            <strong>{metrics.remainingImages.toLocaleString()}</strong>
          </div>
          <span className="media-billing-metric__icon">
            <FaCloud />
          </span>
        </article>
        <article className="media-billing-metric media-billing-metric--coral">
          <div>
            <span>Pending approvals</span>
            <strong>{metrics.pendingApprovals}</strong>
          </div>
          <span className="media-billing-metric__icon">
            <FaShoppingCart />
          </span>
        </article>
      </section>

      <section className="media-settings-panel">
        <div className="media-section-heading">
          <div className="media-section-heading__icon">
            <FaSlidersH />
          </div>
          <div>
            <h2>Billing policy<SettingHelp topic="mediaBillingPolicy" /></h2>
            <p>One allowance for every vendor type in every market. A save applies to the next upload, on every server within 30 seconds.</p>
          </div>
          <span
            className={`media-mode-badge media-mode-badge--${
              settings?.enforcementMode || "observe"
            }`}
          >
            <FaShieldAlt />
            {settings?.enforcementMode || "observe"} mode
          </span>
        </div>

        <div className="media-settings-form">
          <label>
            <span>Free images per vendor<SettingHelp topic="mediaBillingIncludedImages" /></span>
            <input
              type="number"
              min="0"
              value={settingsDraft.includedImages}
              onChange={(event) =>
                setSettingsDraft((current) => ({
                  ...current,
                  includedImages: event.target.value,
                }))
              }
            />
            <small>Each vendor keeps this many images before it needs a paid image pack; approved packs add to it.{settingsDefaults ? ` Default ${settingsDefaults.includedImages}.` : ""}</small>
          </label>
          <label>
            <span>When a vendor runs out of images<SettingHelp topic="mediaBillingEnforcementMode" /></span>
            <AppSelect
              value={settingsDraft.enforcementMode}
              onChange={(event) =>
                setSettingsDraft((current) => ({
                  ...current,
                  enforcementMode: event.target.value,
                }))
              }
            >
              <option value="observe">Observe: count only, never stop an upload</option>
              <option value="warn">Warn: allow the upload (warning not shown yet)</option>
              <option value="block">Block: refuse new uploads until a pack is bought</option>
            </AppSelect>
            <small>What a vendor's next upload meets once its allowance is used up.{settingsDefaults ? ` Default ${settingsDefaults.enforcementMode}.` : ""}</small>
          </label>
          <button
            type="button"
            className="media-button media-button--primary"
            onClick={saveSettings}
            disabled={savingSettings || !settingsDirty}
          >
            {savingSettings ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FaSave />
            )}
            {savingSettings ? "Saving" : "Save policy"}
          </button>
        </div>
      </section>

      <div className="media-billing-grid">
        <section className="media-data-panel media-data-panel--usage">
          <div className="media-data-panel__header">
            <div>
              <h2>Vendor usage</h2>
              <p>
                {filteredRows.length} of {rows.length} vendors shown
              </p>
            </div>
            <label className="media-search">
              <FaSearch />
              <span className="sr-only">Search vendor usage</span>
              <input
                type="search"
                placeholder="Search vendor or type"
                value={usageSearch}
                onChange={(event) => setUsageSearch(event.target.value)}
              />
            </label>
          </div>

          <div className="media-table-scroll">
            <table className="media-table media-usage-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>Usage</th>
                  <th>Allowance</th>
                  <th>Remaining</th>
                  <th>Legacy</th>
                  <th>Storage</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length ? (
                  filteredRows.map((row) => {
                    const allowance = Number(row.totalAllowance || 0);
                    const active = Number(row.activeImages || 0);
                    const usagePercent = allowance
                      ? Math.min(100, Math.round((active / allowance) * 100))
                      : active
                      ? 100
                      : 0;

                    return (
                      <tr key={row._id}>
                        <td>
                          <div className="media-vendor">
                            <span>{row.vendorName?.charAt(0) || "V"}</span>
                            <div>
                              <strong>{row.vendorName || "Vendor"}</strong>
                              <small>{row._id}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="media-type-badge">
                            {(row.vendorType || "unknown").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td>
                          <div className="media-usage">
                            <strong>
                              {active} image{active === 1 ? "" : "s"}
                            </strong>
                            <span>
                              <i style={{ width: `${usagePercent}%` }} />
                            </span>
                          </div>
                        </td>
                        <td className="media-table__number">{allowance}</td>
                        <td>
                          <div className="media-remaining">
                            <strong>{row.remainingImages ?? 0}</strong>
                            {row.reservedUploads ? (
                              <span>{row.reservedUploads} reserved</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="media-table__number">
                          {row.legacyImages ?? 0}
                        </td>
                        <td className="media-table__storage">
                          {fmtBytes(row.storageBytes)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="media-empty">
                        <FaImages />
                        <strong>
                          {usageSearch
                            ? "No vendors match this search"
                            : "No media usage recorded"}
                        </strong>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="media-data-panel media-data-panel--purchases">
          <div className="media-data-panel__header">
            <div>
              <h2>Purchases & approvals</h2>
              <p>{filteredPurchases.length} purchase records</p>
            </div>
            <label className="media-status-filter">
              <span className="sr-only">Filter purchase status</span>
              <AppSelect
                value={purchaseStatus}
                onChange={(event) => setPurchaseStatus(event.target.value)}
              >
                <option value="all">All statuses</option>
                {purchaseStatuses.map((status) => (
                  <option key={status} value={status}>
                    {humanize(status)}
                  </option>
                ))}
              </AppSelect>
            </label>
          </div>

          <div className="media-table-scroll">
            <table className="media-table media-purchases-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Pack</th>
                  <th>Status</th>
                  <th className="media-table__actions-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPurchases.length ? (
                  filteredPurchases.map((purchase) => {
                    const actionable =
                      purchase.status !== "APPROVED" &&
                      purchase.status !== "REJECTED";

                    return (
                      <tr key={purchase._id}>
                        <td>
                          <div className="media-purchase-vendor">
                            <strong>
                              {purchase.vendorNameSnapshot || "Vendor"}
                            </strong>
                            <span>{purchase.payerPhone || "No phone"}</span>
                            <small>
                              {purchase.mtnReferenceId || "No MTN reference"}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="media-pack">
                            <strong>{purchase.imageCredits} images</strong>
                            <span>
                              {fmtMoney(purchase.amount, purchase.currency)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`media-status media-status--${statusTone(
                              purchase.status
                            )}`}
                          >
                            {humanize(purchase.status)}
                          </span>
                        </td>
                        <td>
                          {actionable ? (
                            <div className="media-purchase-actions">
                              <button
                                type="button"
                                className="media-icon-button media-icon-button--approve"
                                onClick={() =>
                                  openPurchaseDialog("approve", purchase)
                                }
                                disabled={busyId === purchase._id}
                                aria-label={`Approve purchase for ${
                                  purchase.vendorNameSnapshot || "vendor"
                                }`}
                                title="Approve purchase"
                              >
                                <FaCheck />
                              </button>
                              <button
                                type="button"
                                className="media-icon-button media-icon-button--reject"
                                onClick={() =>
                                  openPurchaseDialog("reject", purchase)
                                }
                                disabled={busyId === purchase._id}
                                aria-label={`Reject purchase for ${
                                  purchase.vendorNameSnapshot || "vendor"
                                }`}
                                title="Reject purchase"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ) : (
                            <span className="media-action-complete">Complete</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <div className="media-empty">
                        <FaShoppingCart />
                        <strong>No purchases in this view</strong>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="media-data-panel media-audit-panel">
        <div className="media-data-panel__header">
          <div>
            <h2>
              <FaHistory /> Recent audit activity
            </h2>
            <p>Latest policy, quota, and purchase events</p>
          </div>
          <span className="media-audit-count">
            Showing {Math.min(audit.length, 50)}
          </span>
        </div>
        <div className="media-table-scroll">
          <table className="media-table media-audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Vendor</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Subject</th>
              </tr>
            </thead>
            <tbody>
              {audit.length ? (
                audit.slice(0, 50).map((entry) => (
                  <tr key={entry._id}>
                    <td>{fmtDateTime(entry.createdAt)}</td>
                    <td>{entry.vendorId || "Not assigned"}</td>
                    <td>
                      <span className="media-audit-action">
                        {humanize(entry.action)}
                      </span>
                    </td>
                    <td>{entry.actorRole || entry.actorId || "SYSTEM"}</td>
                    <td>
                      {humanize(entry.subjectType)}
                      {entry.subjectId ? (
                        <small>{entry.subjectId}</small>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <div className="media-empty">
                      <FaHistory />
                      <strong>No media audit activity yet</strong>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        show={purchaseDialog.show}
        onHide={closePurchaseDialog}
        centered
        dialogClassName="media-purchase-dialog"
      >
        <Modal.Header closeButton={!busyId}>
          <Modal.Title>
            {purchaseDialog.mode === "approve"
              ? "Approve image pack?"
              : "Reject image pack?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {purchaseDialog.mode === "approve" &&
            purchaseDialog.purchase?.status !== "PAYMENT_SUCCESSFUL" && (
              <div className="media-dialog-warning">
                <FaExclamationTriangle />
                <div>
                  <strong>Payment is not confirmed by MTN.</strong>
                  <span>
                    Continuing records a super-admin approval override.
                  </span>
                </div>
              </div>
            )}
          <div className="media-dialog-summary">
            <span>Vendor</span>
            <strong>
              {purchaseDialog.purchase?.vendorNameSnapshot || "Vendor"}
            </strong>
            <span>Image pack</span>
            <strong>
              {purchaseDialog.purchase?.imageCredits || 0} images for{" "}
              {fmtMoney(
                purchaseDialog.purchase?.amount,
                purchaseDialog.purchase?.currency
              )}
            </strong>
          </div>
          {purchaseDialog.mode === "reject" && (
            <label className="media-dialog-reason">
              <span>Reason for rejection</span>
              <textarea
                rows="3"
                value={purchaseDialog.reason}
                onChange={(event) =>
                  setPurchaseDialog((current) => ({
                    ...current,
                    reason: event.target.value,
                  }))
                }
              />
            </label>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="media-button media-button--ghost"
            onClick={closePurchaseDialog}
            disabled={Boolean(busyId)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`media-button ${
              purchaseDialog.mode === "approve"
                ? "media-button--primary"
                : "media-button--danger"
            }`}
            onClick={confirmPurchaseAction}
            disabled={Boolean(busyId)}
          >
            {busyId ? (
              <Spinner animation="border" size="sm" />
            ) : purchaseDialog.mode === "approve" ? (
              <FaCheck />
            ) : (
              <FaTimes />
            )}
            {busyId
              ? "Processing"
              : purchaseDialog.mode === "approve"
              ? "Approve pack"
              : "Reject pack"}
          </button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default VendorMediaAdmin;
